import { addDaysToIsoDate } from '@/lib/dates/timezoneDayRange'
import { getDashboardDefaultDateRangeForTimezone } from '@/lib/dashboard/dateRange'
import { toCalendarDateStringInTimeZone } from '@/lib/timezone'

export type ReportingDatePresetId =
  | 'today'
  | 'yesterday'
  | 'last_7_days'
  | 'last_30_days'
  | 'this_month'
  | 'last_month'

export const REPORTING_DATE_PRESET_ORDER: readonly ReportingDatePresetId[] = [
  'today',
  'yesterday',
  'last_7_days',
  'last_30_days',
  'this_month',
  'last_month',
] as const

export const REPORTING_DATE_PRESET_LABELS: Record<ReportingDatePresetId, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last_7_days: 'Last 7 days',
  last_30_days: 'Last 30 days',
  this_month: 'This month',
  last_month: 'Last month',
}

/**
 * Pure resolver for reporting date presets. Anchors "today" in the given IANA timezone.
 *
 * - today: from = to = calendar today in zone
 * - yesterday: single calendar day before today in zone
 * - last_7_days: 7 calendar days ending today (from = today − 6)
 * - last_30_days: 30 calendar days ending today (from = today − 29)
 * - this_month: first day of current calendar month in zone through today (month-to-date)
 * - last_month: full previous calendar month in zone (same as dashboard default range helper)
 */
export function resolveReportingDatePresetRange(
  preset: ReportingDatePresetId,
  timeZone: string,
  now: Date = new Date()
): { from: string; to: string } {
  const today = toCalendarDateStringInTimeZone(now, timeZone)

  switch (preset) {
    case 'today':
      return { from: today, to: today }
    case 'yesterday': {
      const y = addDaysToIsoDate(today, -1)
      return { from: y, to: y }
    }
    case 'last_7_days':
      return { from: addDaysToIsoDate(today, -6), to: today }
    case 'last_30_days':
      return { from: addDaysToIsoDate(today, -29), to: today }
    case 'this_month': {
      const [yStr, mStr] = today.split('-')
      const from = `${yStr}-${mStr}-01`
      return { from, to: today }
    }
    case 'last_month':
      return getDashboardDefaultDateRangeForTimezone(timeZone)
    default: {
      const _exhaustive: never = preset
      return _exhaustive
    }
  }
}
